import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'

import { request } from '../api/client'
import { useTenant } from '../api/tenant'
import { Failure } from '../components/Failure'
import { Sparkbars } from '../components/Sparkbars'

interface OverviewData {
  tenant: string
  generated_at: string
  healthy: boolean
  degraded: string[]
  alerts: {
    open: number
    new_24h: number
    closed_24h: number
    suppressed_24h: number
    by_severity: Record<string, number>
    oldest_open_hours: number | null
    unassigned: number
  }
  fleet: {
    agents: number
    approved: number
    pending_approval: number
    silent: number
    silent_hosts: string[]
    silent_after_minutes: number
  }
  pipeline: {
    event_store: {
      reachable: boolean
      detail: string
      events_24h: number | null
      newest_event_age_seconds: number | null
    }
  }
  series: { hour: string; alerts: number }[]
  detection: {
    enabled: number
    disabled: number
    never_backtested: number
    techniques: string[]
  }
}

/**
 * The first screen: is anything wrong, and what needs a person.
 *
 * Built around one refusal — this page never renders a number it does not have. When
 * the event store cannot be reached, the events figure reads "unknown", not zero, and
 * the banner says so. A dashboard that shows zero because a query threw is how a
 * monitoring system stays green for eleven days while collecting nothing.
 */
export function OverviewPage({ tenants = [] }: { tenants?: string[] }) {
  const [tenant] = useTenant(tenants)
  const query = useQuery({
    queryKey: ['overview', tenant],
    queryFn: () =>
      request<OverviewData>(
        `/overview/${tenant ? `?tenant=${encodeURIComponent(tenant)}` : ''}`,
      ),
    refetchInterval: 30_000,
  })

  if (query.isError) return <Failure error={query.error} onRetry={() => query.refetch()} />
  if (!query.data) return <p className="empty">Loading…</p>

  const data = query.data
  const store = data.pipeline.event_store

  return (
    <section aria-labelledby="overview-heading">
      <h2 id="overview-heading">Overview</h2>

      {data.healthy ? (
        <p className="panel" style={{ borderColor: 'var(--ok)' }} role="status">
          Nothing is degraded. Every check on this page returned an answer.
        </p>
      ) : (
        <div className="error" role="alert">
          <strong>{data.degraded.length} thing(s) need attention</strong>
          <ul style={{ marginBottom: 0 }}>
            {data.degraded.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        </div>
      )}

      <div
        style={{
          display: 'grid',
          gap: 'var(--space-4)',
          gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
          marginTop: 'var(--space-4)',
        }}
      >
        <section className="panel" aria-labelledby="alerts-card">
          <h3 id="alerts-card" style={{ marginTop: 0 }}>
            Alerts
          </h3>
          <Stat label="Open" value={data.alerts.open} />
          <Stat label="Unassigned" value={data.alerts.unassigned} />
          <Stat label="New in 24h" value={data.alerts.new_24h} />
          <Stat label="Closed in 24h" value={data.alerts.closed_24h} />
          <Stat
            label="Oldest open"
            value={
              data.alerts.oldest_open_hours === null
                ? 'none open'
                : `${data.alerts.oldest_open_hours} h`
            }
          />
          <Sparkbars points={data.series} label="Alerts per hour" />
          <Link to="/queue">Go to the queue →</Link>
        </section>

        <section className="panel" aria-labelledby="pipeline-card">
          <h3 id="pipeline-card" style={{ marginTop: 0 }}>
            Pipeline
          </h3>
          {/* "unknown", never 0. The distinction is the entire point of this card. */}
          <Stat
            label="Events in 24h"
            value={store.events_24h === null ? 'unknown — store unreachable' : store.events_24h}
            bad={store.events_24h === null || store.events_24h === 0}
          />
          <Stat
            label="Newest event"
            value={
              store.newest_event_age_seconds === null
                ? 'unknown'
                : `${Math.round(store.newest_event_age_seconds / 60)} min ago`
            }
            bad={
              store.newest_event_age_seconds === null || store.newest_event_age_seconds > 600
            }
          />
          <Stat label="Event store" value={store.detail} bad={!store.reachable} />
        </section>

        <section className="panel" aria-labelledby="fleet-card">
          <h3 id="fleet-card" style={{ marginTop: 0 }}>
            Fleet
          </h3>
          <Stat label="Agents" value={data.fleet.agents} />
          <Stat label="Approved" value={data.fleet.approved} />
          <Stat
            label="Awaiting approval"
            value={data.fleet.pending_approval}
            bad={data.fleet.pending_approval > 0}
          />
          <Stat
            label={`Silent > ${data.fleet.silent_after_minutes} min`}
            value={data.fleet.silent}
            bad={data.fleet.silent > 0}
          />
          {data.fleet.silent_hosts.length > 0 && (
            <p className="mono muted">{data.fleet.silent_hosts.join(', ')}</p>
          )}
        </section>

        <section className="panel" aria-labelledby="detection-card">
          <h3 id="detection-card" style={{ marginTop: 0 }}>
            Detection
          </h3>
          <Stat label="Rules enabled" value={data.detection.enabled} />
          <Stat
            label="Rules turned off"
            value={data.detection.disabled}
            bad={data.detection.disabled > 0}
          />
          <Stat label="Never backtested" value={data.detection.never_backtested} />
          <p className="muted">
            Techniques seen: {data.detection.techniques.join(', ') || 'none yet'}
          </p>
          {/* Seen is not the same as covered, and the difference is the interesting
              half: a technique covered by a rule that fired nothing this month, and
              one covered only by a rule this tenant switched off, both read as
              "not seen" here. */}
          <Link to="/coverage">What is covered, and what is not →</Link>
        </section>
      </div>
    </section>
  )
}

function Stat({
  label,
  value,
  bad = false,
}: {
  label: string
  value: string | number
  bad?: boolean
}) {
  return (
    <p style={{ display: 'flex', justifyContent: 'space-between', margin: 'var(--space-1) 0' }}>
      <span className="muted">{label}</span>
      <strong style={bad ? { color: 'var(--bad)' } : undefined}>{value}</strong>
    </p>
  )
}
